// components/reaction-bar.js
// ═══════════════════════════════════════════════════════════
// Message reaction bar. Appears on hover below any message.
// AI messages: Copy, Pin, Ideas, Regenerate, Flag
// User messages: Timestamp, Retry, Copy, Edit
// Three-state icons: rest #888, hover sage, active gold
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';

function ReactionIcon({ children, onClick, title, activeOnClick = false }) {
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);

  function handleClick() {
    if (activeOnClick) setActive(v => !v);
    onClick?.();
  }

  const color = active ? '#c8a96e' : hovered ? '#7a9e82' : '#888888';

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      {hovered && title && (
        <div style={{
          position: 'absolute', top: '100%', left: '50%',
          transform: 'translateX(-50%)', marginTop: 6,
          background: '#7a9e82', borderRadius: 20,
          padding: '3px 10px', whiteSpace: 'nowrap',
          fontSize: 11, color: '#fff', fontFamily: 'Georgia, serif',
          letterSpacing: '0.03em', pointerEvents: 'none', zIndex: 50,
        }}>
          {title}
        </div>
      )}
      <button
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'none', border: 'none',
          cursor: 'pointer', padding: 6,
          borderRadius: 6, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}
      >
        {children(color)}
      </button>
    </div>
  );
}

function CopyButton({ message, onCopy }) {
  const [copied, setCopied] = useState(false);
  return (
    <ReactionIcon
      title={copied ? 'Copied!' : 'Copy'}
      onClick={() => {
        navigator.clipboard?.writeText(message?.content || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        onCopy?.();
      }}
    >
      {c => copied ? (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 10l4 4 8-8" stroke="#7a9e82" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <rect x="7" y="7" width="10" height="12" rx="2" stroke={c} strokeWidth="1.4"/>
          <path d="M4 13H3a2 2 0 01-2-2V3a2 2 0 012-2h8a2 2 0 012 2v1" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      )}
    </ReactionIcon>
  );
}

export default function ReactionBar({ message, onCopy, onPin, onIdea, onRegenerate, onFlag, onEdit }) {
  const isUser = message?.role === 'user';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginTop: 6,
    }}>

      {/* Copy — AI only (user copy is below with timestamp) */}
      {!isUser && (
        <CopyButton message={message} onCopy={onCopy} />
      )}

      {/* User: Timestamp, Retry, Copy, Edit */}
      {isUser && (
        <>
          <span style={{
            color: '#888888', fontSize: 16,
            fontFamily: 'Georgia, serif',
            padding: '0 4px', alignSelf: 'center',
          }}>
            {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
          <ReactionIcon title="Retry" onClick={onRegenerate}>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M4 10a6 6 0 1 0 1.2-3.6" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M2 5l3.2 1.4L4 10" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </ReactionIcon>
          <CopyButton message={message} onCopy={onCopy} />
          <ReactionIcon title="Edit" onClick={onEdit}>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M14.5 2.5a2.121 2.121 0 013 3L6 17H3v-3L14.5 2.5z" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </ReactionIcon>
        </>
      )}

      {/* AI only: Pin, Ideas, Regenerate, Flag */}
      {!isUser && (
        <>
          <ReactionIcon title="Pin to artifacts" onClick={onPin} activeOnClick>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M12 2l1.5 1.5-1 1 2 2 1-1L17 7l-3.5 3.5c.3 1 .1 2.1-.5 3l-1 1-3-3-4 4-1.5-1.5 4-4-3-3 1-1c.9-.6 2-.8 3-.5L12 2z" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </ReactionIcon>

          <ReactionIcon title="Send to Ideas" onClick={onIdea} activeOnClick>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M10 2.5C7.24 2.5 5 4.74 5 7.5c0 1.8.92 3.38 2.32 4.32.4.27.68.7.68 1.18v1a1 1 0 001 1h2a1 1 0 001-1v-1c0-.48.28-.91.68-1.18C14.08 10.88 15 9.3 15 7.5c0-2.76-2.24-5-5-5z" stroke={c} strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M7.5 17h5" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            )}
          </ReactionIcon>

          <ReactionIcon title="Regenerate" onClick={onRegenerate}>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M4 10a6 6 0 1 0 1.2-3.6" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M2 5l3.2 1.4L4 10" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </ReactionIcon>

          <ReactionIcon title="Flag" onClick={onFlag} activeOnClick>
            {c => (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M4 3v14M4 3h10l-2.5 4L14 11H4" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </ReactionIcon>
        </>
      )}
    </div>
  );
}